import { buildCompanyFilters } from '@/utils/company-search.utils';
import { buildCrustdataFilters } from '@/containers/search/people-search-hook';
import { normalizeDomain, normalizeDomainList } from '@/lib/normalize-domain';
import { crmAccountViewValid } from './config/company-signal.util';

import {
  MAX_TARGET_DOMAINS,
  type AgentPeopleFilters,
  type AgentCompanyFilters,
} from './search-audience-fields';
import { BRAND } from '@/lib/brand';
export { fundingFiltered } from './search-audience-fields';
export type {
  AgentPeopleFilters,
  AgentCompanyFilters,
} from './search-audience-fields';

function rolling(days: number | undefined, fixed: string, label: string) {
  if (days === undefined) return;
  if (!Number.isInteger(days) || days < 1 || days > 3650)
    throw new Error(`${label} must be 1 to 3650 whole days`);
  if (fixed)
    throw new Error(`audience_conflict: choose a fixed date or ${label}`);
}

/** Save internal tokens. The server resolves them in the play's local day,
 *  and reads the target accounts at every check. */
export function buildAgentPeopleFilters(filters: AgentPeopleFilters) {
  rolling(
    filters.joinedCurrentWithinDays,
    filters.joinedCurrentAfter,
    'Joined within'
  );
  const crm = filters.currentCompanyDomainsFromCrm;
  const list = filters.currentCompanyDomainsFromList;
  if (list) {
    if (crm)
      throw new Error(
        'audience_conflict: choose a company list or CRM accounts'
      );
    if (
      filters.currentCompanyDomains.length ||
      filters.currentCompanyNames.length ||
      filters.currentCompanyLinkedInUrls.length
    )
      throw new Error(
        'audience_conflict: choose a company list or named current companies'
      );
    if (
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        list.listId
      )
    )
      throw new Error(`Pick a valid ${BRAND.name} list`);
  }
  if (crm) {
    if (
      filters.currentCompanyDomains.length ||
      filters.currentCompanyNames.length ||
      filters.currentCompanyLinkedInUrls.length
    )
      throw new Error(
        'audience_conflict: choose CRM accounts or named current companies'
      );
    if (!crmAccountViewValid(crm))
      throw new Error('Choose a CRM account condition');
  }
  const wire = buildCrustdataFilters(filters);
  const tokens: Record<string, unknown>[] = [];
  if (filters.joinedCurrentWithinDays !== undefined)
    tokens.push({
      column: 'joined_current_after',
      type: '=>',
      value: { relativeDays: filters.joinedCurrentWithinDays },
    });
  // Same order and shape as the API's compile_agent_search.
  if (list)
    tokens.push({
      column: 'current_employers.company_website_domain',
      type: 'in',
      value: { listId: list.listId },
    });
  if (crm)
    tokens.push({
      column: 'current_employers.company_website_domain',
      type: 'in',
      value: {
        crmAccounts: { crmType: crm.crmType, condition: crm.condition },
      },
    });
  if (!tokens.length) return wire;
  return {
    ...wire,
    filters: [
      ...((wire.filters as Record<string, unknown>[] | undefined) ?? []),
      ...tokens,
    ],
  };
}

const COMPANY_IDENTITY_COLUMNS = new Set([
  'company_name',
  'company_website_domain',
  'domains',
  'linkedin_profile_url',
]);

/** A company identity leaf, or the OR group buildCompanyFilters makes of
 *  them. */
function isCompanyIdentity(node: Record<string, unknown>): boolean {
  if (node.op === 'or' && Array.isArray(node.conditions))
    return (node.conditions as Record<string, unknown>[]).every(
      isCompanyIdentity
    );
  return COMPANY_IDENTITY_COLUMNS.has(node.filter_type as string);
}

/**
 * A target-account list is one more way the Company row names companies,
 * so any match counts: it joins the names and LinkedIn pages' OR group (or
 * makes one with a single name or page) instead of ANDing with them. Alone
 * it is appended. Same as the API's `_add_named_accounts` (f15f36648), so
 * the saved wire matches.
 */
function addNamedAccounts(
  conditions: Record<string, unknown>[],
  token: Record<string, unknown>
) {
  const index = conditions.findIndex(
    (node) => node && typeof node === 'object' && isCompanyIdentity(node)
  );
  if (index === -1) {
    conditions.push(token);
    return;
  }
  const node = conditions[index];
  conditions[index] =
    node.op === 'or'
      ? { ...node, conditions: [...(node.conditions as unknown[]), token] }
      : { op: 'or', conditions: [node, token] };
}

export function buildAgentCompanyFilters(
  filters: AgentCompanyFilters,
  boundedTargets = true
) {
  rolling(
    filters.lastFundingWithinDays,
    filters.lastFundingDateAfter,
    'Funding within'
  );
  rolling(filters.ipoWithinDays, filters.ipoDateAfter, 'IPO within');
  if (
    (boundedTargets ||
      filters.lastFundingWithinDays !== undefined ||
      filters.ipoWithinDays !== undefined ||
      filters.domainsFromList) &&
    filters.domainsList.length > MAX_TARGET_DOMAINS
  )
    throw new Error('Target at most 500 company domains');
  if (filters.domainsFromList && filters.domainsList.length)
    throw new Error(
      `audience_conflict: choose pasted domains or a ${BRAND.name} list`
    );
  if (
    filters.domainsFromList &&
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      filters.domainsFromList.listId
    )
  )
    throw new Error(`Pick a valid ${BRAND.name} list`);
  const domainsList = normalizeDomainList(filters.domainsList);
  if (filters.domainsList.length && !domainsList.length)
    throw new Error('Paste at least one valid company domain');
  const wire = buildCompanyFilters({ ...filters, domainsList });
  const conditions = [
    ...((wire.conditions as Record<string, unknown>[] | undefined) ?? []),
  ];
  for (const [column, days] of [
    ['last_funding_date', filters.lastFundingWithinDays],
    ['ipo_date', filters.ipoWithinDays],
  ] as const) {
    if (days !== undefined)
      conditions.push({
        filter_type: column,
        type: '=>',
        value: { relativeDays: days },
      });
  }
  if (filters.domainsFromList)
    addNamedAccounts(conditions, {
      filter_type: 'domains',
      type: 'in',
      value: { listId: filters.domainsFromList.listId },
    });
  if (filters.lookalikeOf) {
    const { domains, refreshDays } = filters.lookalikeOf;
    if (
      !Array.isArray(domains) ||
      domains.length < 1 ||
      domains.length > 25 ||
      domains.some(
        (domain) => typeof domain !== 'string' || !normalizeDomain(domain)
      )
    )
      throw new Error('Choose 1 to 25 valid seed company domains');
    if (!Number.isInteger(refreshDays) || refreshDays < 1 || refreshDays > 3650)
      throw new Error('Refresh lookalike facets every 1 to 3650 whole days');
    conditions.push({
      filter_type: 'lookalike',
      type: 'in',
      value: { domains: normalizeDomainList(domains), refreshDays },
    });
  }
  return conditions.length ? { op: 'and', conditions } : {};
}
